import { Prop, Schema, SchemaFactory } from '@nestjs/mongoose';
import { Document, Types } from 'mongoose';
import { OrderStatus } from '../../../common/enums/order-status.enum';
import { BaseMongoEntity } from '@/common/entities/base.entity';
import { IsOptional } from 'class-validator';
import { MultilingualTextSchema } from '@/common/schemas/multilingual-text.schema';
import { PaymentMethod, PaymentStatus } from '../enums/order-item-type.enum';

export type OrderDocument = Order & Document;

export interface OrderItem {
  itemType: 'Product' | 'Package';
  itemId: Types.ObjectId;
  quantity: number;
  price?: number;
  totalPrice?: number;
  discountPrice?: number;
  color?: string;
  size?: string;
  packageItems?: Array<{
    productId: Types.ObjectId;
    quantity: number;
    selectedVariants: Array<{
      variant: string;
      value: { en: string; ar: string };
    }>;
  }>;
  selectedVariants?: Array<{
    variant: string;
    value: { en: string; ar: string };
  }>;
}

@Schema({ timestamps: true })
export class Order extends BaseMongoEntity {
  @Prop({ type: Types.ObjectId, ref: 'User', required: false })
  @IsOptional()
  customerId?: Types.ObjectId;

  @Prop({ type: Types.ObjectId, ref: 'User', required: false })
  @IsOptional()
  user?: Types.ObjectId;

  @Prop({
    type: [{
      itemType: { type: String, enum: ['Product', 'Package'], required: true, default: 'Product' },
      itemId: { type: Types.ObjectId, required: true, refPath: 'items.itemType' },
      quantity: Number,
      price: Number,
      totalPrice: Number,
      discountPrice: Number,
      color: String,
      size: String,
      packageItems: [{
        productId: { type: Types.ObjectId, ref: 'Product' },
        quantity: Number,
        selectedVariants: [{
          variant: String,
          value: { type: MultilingualTextSchema, required: true }
        }]
      }],
      selectedVariants: [{
        variant: String,
        value: { type: MultilingualTextSchema, required: true }
      }]
    }],
    required: true
  })
  items: OrderItem[];

  @Prop({ type: Number, required: true })
  subtotal: number;

  @Prop({ type: Number, default: 0 })
  tax: number;

  @Prop({ type: Number, default: 0 })
  shippingCost: number;

  @Prop({ type: Number, default: 0 })
  discount: number;

  @Prop({ type: Number, default: 0 })
  shippingDiscount: number;

  @Prop({ type: Number, required: true })
  total: number;

  @Prop({ type: String, enum: PaymentStatus, default: PaymentStatus.PENDING })
  paymentStatus: PaymentStatus;

  @Prop({ type: String, enum: OrderStatus, default: OrderStatus.PENDING })
  orderStatus: OrderStatus;

  @Prop({ type: String, required: true, unique: true })
  orderNumber: string;

  @Prop({ type: String, required: false })
  orderId?: string;

  @Prop({ type: Date, required: false })
  orderDate?: Date;
  
  @Prop({
    type: {
      fullName: String,
      address: String,
      city: String,
      state: { type: Types.ObjectId, ref: 'State' },
      country: { type: Types.ObjectId, ref: 'Country' },
      phone: String,
    },
    required: true
  })
  shippingAddress: {
    fullName: string;
    address: string;
    city: string;
    state: Types.ObjectId | string;
    country: Types.ObjectId | string;
    phone?: string;
  };

  @Prop({ type: Types.ObjectId, ref: 'Coupon', default: null })
  @IsOptional()
  coupon?: Types.ObjectId;

  @Prop({
    type: {
      code: { type: String },
      discountType: { type: String, enum: ['percentage', 'fixed_amount'] },
      value: { type: Number, default: 0 },
      isShippingDiscount: { type: Boolean, default: false },
      discountAmount: { type: Number, default: 0 },
      shippingDiscountAmount: { type: Number, default: 0 }
    },
    _id: false
  })
  @IsOptional()
  couponDetails?: {
    code?: string;
    discountType?: string;
    value?: number;
    isShippingDiscount?: boolean;
    discountAmount?: number;
    shippingDiscountAmount?: number;
  };

  @Prop({
    type: {
      amountPaid: { type: Number, default: 0 },
      changeDue: { type: Number, default: 0 },
      paymentImage: { type: String, default: '' },
    },
    default: null,
    _id: false
  })
  cashPayment: {
    amountPaid?: number;
    changeDue?: number;
    paymentImage?: string;
  };

  @Prop({ type: String, enum: PaymentMethod, default: PaymentMethod.CASH })
  paymentMethod: PaymentMethod;

  @Prop({ type: Boolean, default: false })
  isDeposit?: boolean;

  @Prop({ type: String })
  notes?: string;

  @Prop({ type: Date })
  deliveredAt?: Date;

  @Prop({ type: Date })
  deliveryDate?: Date;
}

export const OrderSchema = SchemaFactory.createForClass(Order);

OrderSchema.index({ orderNumber: 1 });
OrderSchema.index({ user: 1 });
OrderSchema.index({ orderStatus: 1 });
OrderSchema.index({ paymentStatus: 1 });
